import { useCallback, useContext } from 'react'
import { useQuery, useMutation } from 'react-query'
import { apiGet, apiPost, apiPatch, apiDelete } from 'api'
import AuthContext from 'context/auth-context'
import UserContext from 'context/user-context'

const getFolders = async (token, userId, kind, sectionId) => {
  const url = `users/${userId}/folders`
  const data = await apiGet({ url, token, params: { kind, section_id: sectionId || undefined } })
  return data
}

// `kind` scopes the list to one section ('views' | 'reports'): folders created for it, plus every
// folder that belongs to no section in particular (which is every folder made by hand). Omit it to
// get all of them.
//
// `sectionId` (one id, or the comma-separated list the module chips produce) scopes the returned
// item counts to those sections, so the caller can tell a folder with matching work in it from one
// with none.
//
// The key carries both so different scopings don't share a cache entry;
// invalidateQueries('folders') still matches them all by prefix.
export function useFolders({ kind, sectionId, options = {} } = {}) {
  const token = useContext(AuthContext)
  const { id } = useContext(UserContext)
  const queryFn = () => getFolders(token, id, kind, sectionId)
  const queryKey = ['folders', kind ?? 'all', sectionId || 'all-sections']

  return useQuery(queryKey, queryFn, { ...options })
}

const createFolder = async (token, userId, params) => {
  const url = `users/${userId}/folders`
  const data = await apiPost({ url, token, objectName: 'folder', params })
  return data
}

export function useCreateFolder({ options = {} } = {}) {
  const token = useContext(AuthContext)
  const { id } = useContext(UserContext)

  return useMutation(
    (params) => createFolder(token, id, params),
    { ...options }
  )
}

const updateFolder = async (token, id, params) => {
  const url = `folders/${id}`
  const data = await apiPatch({ url, token, objectName: 'folder', params })
  return data
}

export function useUpdateFolder({ options = {} } = {}) {
  const token = useContext(AuthContext)

  return useMutation(
    ({ id, ...params }) => updateFolder(token, id, params),
    { ...options }
  )
}

const destroyFolder = async (token, id) => {
  const url = `folders/${id}`
  const data = await apiDelete({ url, token })
  return data
}

export function useDestroyFolder({ options = {} } = {}) {
  const token = useContext(AuthContext)

  return useMutation(
    (id) => destroyFolder(token, id),
    { ...options }
  )
}

const moveItem = async (token, userId, params) => {
  const url = `users/${userId}/folders/move_item`
  const data = await apiPatch({ url, token, objectName: 'move', params })
  return data
}

export function useMoveItem({ options = {} } = {}) {
  const token = useContext(AuthContext)
  const { id } = useContext(UserContext)

  return useMutation(
    (params) => moveItem(token, id, params),
    { ...options }
  )
}

// What else belongs with an item: the report it is part of, that report's dataset Views, and the
// Samples attached to it. Fetched on demand at the moment of a move rather than kept in a query
// cache — the answer is only ever wanted once, right after a drop, and it has to be current.
export function useFetchRelatedItems() {
  const token = useContext(AuthContext)
  const { id } = useContext(UserContext)

  return useCallback(
    (params) => apiGet({ url: `users/${id}/folders/related_items`, token, params }),
    [token, id]
  )
}
